import {
	print,
	type Jast,
	type OrderByField,
	ORDER_BY_DIRECTION_DESC,
	type OrderByDirectionValue,
	creators,
} from '@atlaskit/jql-ast';
import { fg } from '@atlassian/jira-feature-gating';
import type { SortOrder } from '../types.tsx';
import { substring } from './common.tsx';
import { sanitizeClauseName } from './sanitize-clause-name.tsx';

export const setPrimarySortField = (
	jast: Jast,
	sortField: string,
	sortDirection: SortOrder = ORDER_BY_DIRECTION_DESC,
	aliases: string[],
): string => {
	const orderBy = jast.query?.orderBy;
	const jql = jast.represents;

	// #region New implementation with JQL AST refactor
	if (fg('jsc_list_reranking_ast_refactor')) {
		// If the sortDirection provided is 'NONE', the sortField should be removed and not added.
		const orderByDirection: OrderByDirectionValue | undefined =
			sortDirection === 'ASC' || sortDirection === 'DESC' ? sortDirection : undefined;

		// Add order by clause if it doesn't exist
		if (!orderBy || !orderBy.fields.length) {
			// sortField is only prepended if a valid orderByDirection is provided
			orderByDirection &&
				jast.query?.prependOrderField(
					creators.orderByField(
						creators.field(sortField),
						creators.orderByDirection(orderByDirection),
					),
				);
			return print(jast);
		}

		let currentSortField: string = sortField;
		const fieldAliases = new Set([sanitizeClauseName(sortField).toLocaleLowerCase(), ...aliases]);
		const visitedFields = new Set<string>();

		// Filter out duplicate fields
		const filteredFields = orderBy.fields.filter((field) => {
			const sanitizedFieldName = sanitizeClauseName(field.field.value).toLocaleLowerCase();
			if (fieldAliases.has(sanitizedFieldName)) {
				currentSortField = field.field.value;
				return false;
			}
			if (visitedFields.has(sanitizedFieldName)) {
				return false;
			}
			visitedFields.add(field.field.value);
			return true;
		});

		// Remove orderBy clause if there are no fields left and no valid orderByDirection
		if (!filteredFields.length && !orderByDirection) {
			orderBy.remove();
			return print(jast);
		}

		// Replace the orderBy clause with the updated fields
		orderBy.replace(creators.orderBy(filteredFields));
		// sortField is only prepended if a valid orderByDirection is provided
		orderByDirection &&
			jast.query?.prependOrderField(
				creators.orderByField(
					creators.field(currentSortField),
					creators.orderByDirection(orderByDirection),
				),
			);
		return print(jast);
	}

	// #region Legacy implementation with string manipulation
	if (!orderBy || !orderBy.fields.length) {
		return sortDirection === 'NONE' && fg('jsc_list_reranking_gate')
			? jql
			: `${jql} ORDER BY ${sortField}${sortDirection ? ` ${sortDirection}` : ''}`;
	}

	const includedFields = new Set([sortField, ...aliases]);
	const sanitizedSortField = sanitizeClauseName(sortField);

	const orderByFields: OrderByField[] = orderBy.fields;

	// Filter out duplicate fields based on their value in the JQL
	const filteredFields = orderByFields.filter((field) => {
		const { position } = field.field;
		const fieldValue =
			field.field.properties && position
				? substring(jql, position[0], position[1])
				: field.field.value;
		if (includedFields.has(fieldValue)) {
			return false;
		}
		includedFields.add(fieldValue);
		return true;
	});

	// remove order by clause if there are no fields left
	if (!filteredFields.length && sortDirection === 'NONE' && fg('jsc_list_reranking_gate')) {
		orderBy.remove();
		return print(jast);
	}

	// Convert fields to JQL
	const fieldsJql = filteredFields
		.reduce(
			(acc, field) => {
				const { position } = field;
				if (position) {
					acc.push(substring(jql, position[0], position[1]));
				}
				return acc;
			},
			!fg('jsc_list_reranking_gate') || sortDirection !== 'NONE'
				? [`${sanitizedSortField} ${sortDirection}`]
				: [],
		)
		.join(', ');

	const startPosition = orderBy.fields[0].position;
	if (startPosition) {
		const start = startPosition[0];
		return `${substring(jql, 0, start)}${fieldsJql}`;
	}
	return jql;
};
